import React from "react";
import { useState } from "react";
import { useEffect } from "react";
import { useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Grid, Box, CssBaseline } from "@mui/material";

import DetailsPageHeader from "../../../../common/DetailsPageHeader";
import { mainLayoutStyles, showNotification } from "../../../../constants";
import Sidebar from "../../../../common/Sidebar";
import {
  DetailsPageBottomCardSection,
  DetailsPageTopCardSection,
} from "../../components/components";
import ImagePreviewModal from "../../../../common/ImagePreviewModal";
import AudioModal from "../../../../common/AudioModal";
import ConfirmationModal from "../../../../common/ConfirmationModal";
import { useQuickTalesContext } from "../../../../providers/OrganizationAdmin/QuickTalesProvider";
import { useOrganizationContext } from "../../../../providers/OrganizationAdmin/OrganizationProvider";
import { useGetEmailTemplates } from "../../../OrganizationEmailTemplates/hooks/EmailTemplatesHooks";
import { useShareActivityEvents } from "../../../OrganizationBatches/hooks/BatchQuickTalesHooks";
import ShareActivityTable from "../../../OrganizationBatches/components/ShareActivityTable/ShareActivityTable";
import { httpsCallable } from "firebase/functions";
import {
  updateQuickTale,
  uploadQuickTaleAudioToStorage,
  uploadQuickTaleImageToStorage,
  getSingleQuickTale,
  getSingleUser,
} from "../../../OrganizationQuickTales/hooks/QuickTalesHooks";
import {
  createCustomNarrator,
  narratorNameExists,
  getCustomNarratorsByUser,
  getCustomNarratorByName,
  updateCustomNarrator,
} from "../../../OrganizationQuickTales/hooks/CustomNarratorsHooks";
import {
  getAreaMembershipDiff,
  removeQuickTaleFromAlbumsAndQTs,
  upsertQuickTaleInAlbumsAndQTs,
} from "../../../OrganizationQuickTales/components/QTDetailsDataFieldsSection/QTAreasField";
import { getOrgAreas, updateArea } from "../../../AreaManagement/hooks/AreaHook";
import { resolveCreatedByData } from "../../../../utils/createdByUtils";

import { Toast } from "../../../../common/alert";
import { useAuth } from "../../../../hooks";
import { GeoPoint, Timestamp } from "firebase/firestore";
import moment from "moment";
import { insertQTAddSearchDocumentData, functions, auth } from "../../../../firebase";

const QuickTaleToOrganizationDetailsContainer = () => {
  const { currentUser } = useAuth();
  const {
    handleGetAllQuickTales,
    loading,
    setLoading,
    // insertQTAddSearchDocumentData,
  } = useQuickTalesContext();
  const { currentOrganization } = useOrganizationContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { quicktaleId: paramQuicktaleId } = useParams(); // Get ID from URL params
  const [quicktaleDetails, setQuicktaleDetails] = useState({});

  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [audioModal, setAudioModal] = useState(false);
  const [editEnabled, setEditEnabled] = useState(false);
  const [uploadImage, setUploadImage] = useState(null);
  const [uploadAudioFile, setUploadAudioFile] = useState(null);
  const [cancelConfirmationModalOpen, setCancelConfirmationModalOpen] = useState(false);
  const [narratorError, setNarratorError] = useState(false);

  // Use a local saving state for the header Save button so it doesn't get stuck
  // due to other operations (Send Link / Get Link / initial fetch) toggling the
  // shared QuickTalesContext `loading` state.
  const [saving, setSaving] = useState(false);

  // Keep a baseline for area membership so we can diff on Save (org-only).
  // QTAreasField initializes/syncs quicktaleDetails.uiAreaIds; this ref stores
  // the initial membership so we know what to add/remove on the area docs.
  const initialAreaIdsRef = useRef(null);


  // Email template selection states
  const [templateSelectionModalOpen, setTemplateSelectionModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [pendingNarratorData, setPendingNarratorData] = useState(null); // Stores narrator data while template is being selected

  // Narrator/email details for prompts
  const [emailPromptModalOpen, setEmailPromptModalOpen] = useState(false);
  const [narratorEmail, setNarratorEmail] = useState("");
  const [narratorFirstName, setNarratorFirstName] = useState("");
  const [narratorLastName, setNarratorLastName] = useState("");
  const [customNarratorDoc, setCustomNarratorDoc] = useState(null);

  // Get Link modal state
  const [getLinkModalOpen, setGetLinkModalOpen] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");
  const [isCopied, setIsCopied] = useState(false);

  // Get Link template selection states
  const [getLinkTemplateSelectionModalOpen, setGetLinkTemplateSelectionModalOpen] = useState(false);
  const [selectedGetLinkTemplate, setSelectedGetLinkTemplate] = useState("");

  // Fetch email templates for the organization
  const { templates: allEmailTemplates } = useGetEmailTemplates(quicktaleDetails?.organization);

  // Filter to only show active templates
  const activeEmailTemplates = allEmailTemplates?.filter((t) => t.status === "active") || [];

  // Share activity tracking — real-time listener for the subcollection
  const { events: shareActivityEvents, loading: shareActivityLoading } = useShareActivityEvents(quicktaleDetails?.id, 'quicktales');

  // Auto-select default email template (or single active template) when a template
  // selection modal is opened and no template has been chosen yet.
  useEffect(() => {
    if (!(templateSelectionModalOpen || emailPromptModalOpen)) return;
    if (!activeEmailTemplates || activeEmailTemplates.length === 0) return;
    if (selectedTemplate) return; // Don't override user choice

    const defaultTemplate = activeEmailTemplates.find((t) => t.default === true);
    if (defaultTemplate) {
      setSelectedTemplate(defaultTemplate.id);
    } else if (activeEmailTemplates.length === 1) {
      setSelectedTemplate(activeEmailTemplates[0].id);
    }
  }, [
    templateSelectionModalOpen,
    emailPromptModalOpen,
    activeEmailTemplates,
    selectedTemplate,
  ]);

  // Auto-select default email template (or single active template) when the Get Link
  // template selection modal is opened and no template has been chosen yet.
  useEffect(() => {
    if (!getLinkTemplateSelectionModalOpen) return;
    if (!activeEmailTemplates || activeEmailTemplates.length === 0) return;
    if (selectedGetLinkTemplate) return;

    const defaultTemplate = activeEmailTemplates.find((t) => t.default === true);
    if (defaultTemplate) {
      setSelectedGetLinkTemplate(defaultTemplate.id);
    } else if (activeEmailTemplates.length === 1) {
      setSelectedGetLinkTemplate(activeEmailTemplates[0].id);
    }
  }, [
    getLinkTemplateSelectionModalOpen,
    activeEmailTemplates,
    selectedGetLinkTemplate,
  ]);

  const handleImagePreviewModalOpen = () => {
    setImagePreviewModal(true);
  };
  const handleImagePreviewModalClose = () => {
    setImagePreviewModal(false);
  };

  const handleAudioModalOpen = () => {
    setAudioModal(true);
  };
  const handleAudioModalClose = () => {
    setAudioModal(false);
  };

  const handleCancelClick = () => {
    setCancelConfirmationModalOpen(true);
  };

  const handleConfirmCancel = async () => {
    setCancelConfirmationModalOpen(false);
    setEditEnabled(false);
    // Reset any uploaded files
    setUploadImage(null);
    setUploadAudioFile(null);
    // Refresh the data from server to remove any unsaved changes
    try {
      setLoading(true);
      const freshData = await getSingleQuickTale(quicktaleDetails.id);
      if (freshData.exists()) {
        setQuicktaleDetails({ id: quicktaleDetails.id, ...freshData.data() });
      } else {
        // Fallback to resetting from location state
        setQuicktaleDetails(JSON.parse(JSON.stringify(location.state.quicktaleDetails)));
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
      // Fallback to resetting from location state
      setQuicktaleDetails(JSON.parse(JSON.stringify(location.state.quicktaleDetails)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchQuickTale = async (id) => {
      console.log("Attempting to fetch QuickTale with ID:", id);
      setLoading(true);
      try {
        const doc = await getSingleQuickTale(id);
        if (doc.exists()) {
          console.log("QuickTale fetched successfully:", doc.id, doc.data());
          const docData = doc.data();
          
          // Resolve "Created By": the stored id can be a users.id OR an
          // organizations.id. The shared helper tries users first, then falls
          // back to organizations (using the current org list when available).
          const resolvedCreatedByData = await resolveCreatedByData(
            docData.createdBy,
            currentOrganization ? [currentOrganization] : null
          );
          const narratorTemp = await getSingleUser(docData.narrator);
          
          setQuicktaleDetails({
            id: doc.id,
            ...docData,
            createdByData: resolvedCreatedByData,
            narratorData: narratorTemp?.data() ? narratorTemp.data() : null,
            // Initialize userNoProcess based on processedAudioUrl
            userNoProcess: docData.processedAudioUrl ? false : true,
          });
        } else {
          console.error("QuickTale not found in DB for ID:", id);
          navigate('/add-quick-tales-to-organization', { replace: true });
        }
      } catch (error) {
        console.error("Error fetching QuickTale from DB:", error);
        navigate('/add-quick-tales-to-organization', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    let currentQuicktaleId = paramQuicktaleId;
    console.log("paramQuicktaleId:", paramQuicktaleId);
    console.log("location.state.quicktaleDetails?.id:", location?.state?.quicktaleDetails?.id);

    // If paramQuicktaleId is the literal route parameter name, treat it as undefined
    if (currentQuicktaleId === ':quicktaleId') {
      currentQuicktaleId = undefined;
      console.log("paramQuicktaleId was literal ':quicktaleId', treating as undefined.");
    }

    if (!currentQuicktaleId && location?.state?.quicktaleDetails?.id) {
      // Fallback to location.state if ID not in URL params
      currentQuicktaleId = location.state.quicktaleDetails.id;
      setQuicktaleDetails(location.state.quicktaleDetails); // Initialize state to avoid re-fetch if data is already present
      console.log("Using QuickTale ID from location.state:", currentQuicktaleId);
    } else if (currentQuicktaleId) {
      console.log("Using QuickTale ID from URL params:", currentQuicktaleId);
    }

    if (currentQuicktaleId) {
      fetchQuickTale(currentQuicktaleId);
    } else {
      console.log("No QuickTale ID found in URL params or location.state. Redirecting.");
      navigate('/add-quick-tales-to-organization', { replace: true });
    }
  }, [paramQuicktaleId, location.state, navigate, setLoading]);

  // Render null while data is being initially fetched or redirected.
  // Do NOT gate on `loading` alone, so actions like Send Link / Get Link
  // (which toggle loading) don't briefly blank the screen.
  if (!quicktaleDetails?.id && (paramQuicktaleId || location?.state?.quicktaleDetails?.id)) {
    return null; // Or a loading spinner
  }

  // If after all checks, there's still no quicktaleDetails.id, it means we couldn't find it or were redirected.
  // This return null is a safeguard, as the useEffect should handle navigation.
  if (!quicktaleDetails?.id) {
    return null;
  }

  const sendLinkToNarrator = async (email, firstName, lastName, templateId) => {
    setLoading(true);
    try {
      const sendExternalUpdateLink = httpsCallable(functions, "sendExternalUpdateLink");
      await sendExternalUpdateLink({
        quicktaleId: quicktaleDetails.id,
        email,
        firstName,
        lastName,
        quickTale: {
          title: quicktaleDetails.title,
        },
        origin: window.location.origin,
        emailTemplateId: templateId,
        orgName: currentOrganization?.name || "",
      });
      showNotification("Update link sent successfully.");
    } catch (error) {
      Toast("error", `Error sending link: ${error.message}`);
      console.error("Error calling sendExternalUpdateLink:", error);
    } finally {
      setLoading(false);
      setSelectedTemplate("");
      setPendingNarratorData(null);
    }
  };

  const handleSendLink = async () => {
    if (!quicktaleDetails.narrator) {
      showNotification("Told By is required to send a link.");
      return;
    }
    // Case 1: Narrator is a registered user with email
    if (quicktaleDetails.narratorData && quicktaleDetails.narratorData.email) {
      // Store narrator data and open template selection modal
      setPendingNarratorData({
        email: quicktaleDetails.narratorData.email,
        firstName: quicktaleDetails.narratorData.firstName,
        lastName: quicktaleDetails.narratorData.lastName,
      });
      // Auto-select if only one template
      if (activeEmailTemplates.length === 1) {
        setSelectedTemplate(activeEmailTemplates[0].id);
      }
      setTemplateSelectionModalOpen(true);
      return;
    }

    // Case 2: Narrator is a custom narrator
    if (!quicktaleDetails.narratorData && quicktaleDetails.narrator) {
      const narratorName = quicktaleDetails.narrator.trim();
      const userIdToUse = currentUser?.userId || currentUser?.uid;
      
      try {
        const customNarrator = await getCustomNarratorByName(
          narratorName,
          userIdToUse
        );

        // Always prompt for narrator details so First Name, Last Name, and
        // Email Address are visible and editable, regardless of whether
        // values already exist.
        setCustomNarratorDoc(customNarrator);
        setNarratorEmail(customNarrator?.email || "");
        setNarratorFirstName(customNarrator?.firstName || "");
        setNarratorLastName(customNarrator?.lastName || "");
        setEmailPromptModalOpen(true);
      } catch (error) {
        Toast("error", "Could not find custom narrator.");
        console.error("Error fetching custom narrator:", error);
      }
      return;
    }

    Toast("error", "Narrator has no email address.");
  };

  // Handle template selection modal continue
  const handleTemplateSelectionContinue = async () => {
    if (!selectedTemplate) {
      showNotification("Please select an email template.");
      return;
    }
    
    setTemplateSelectionModalOpen(false);
    
    if (pendingNarratorData) {
      await sendLinkToNarrator(
        pendingNarratorData.email,
        pendingNarratorData.firstName,
        pendingNarratorData.lastName,
        selectedTemplate
      );
    }
  };

  // Handle template selection modal close
  const handleTemplateSelectionClose = () => {
    setTemplateSelectionModalOpen(false);
    setSelectedTemplate("");
    setPendingNarratorData(null);
  };

  const handleEmailPromptContinue = async () => {
    // Validate all required fields
    if (!narratorFirstName) {
      showNotification("Please enter a first name.");
      return;
    }
    if (!narratorEmail) {
      showNotification("Please enter an email address.");
      return;
    }
    // Validate template selection (if templates are available)
    if (activeEmailTemplates.length > 0 && !selectedTemplate) {
      showNotification("Please select an email template.");
      return;
    }
    
    setEmailPromptModalOpen(false);

    try {
      // Update the custom narrator record with all the new details
      if (customNarratorDoc && customNarratorDoc.id) {
        await updateCustomNarrator(customNarratorDoc.id, { 
          email: narratorEmail,
          firstName: narratorFirstName,
          lastName: narratorLastName,
        });
        await sendLinkToNarrator(
          narratorEmail,
          narratorFirstName,
          narratorLastName,
          selectedTemplate
        );
      } else {
        throw new Error("Custom narrator document not found.");
      }
    } catch (error) {
      Toast("error", `Failed to save narrator details: ${error.message}`);
      console.error("Error updating custom narrator:", error);
    }
    // Reset all inputs
    setNarratorEmail("");
    setNarratorFirstName("");
    setNarratorLastName("");
    setSelectedTemplate("");
  };
  
  // Handle email prompt modal close
  const handleEmailPromptClose = () => {
    setEmailPromptModalOpen(false);
    setNarratorEmail("");
    setNarratorFirstName("");
    setNarratorLastName("");
    setSelectedTemplate("");
    setCustomNarratorDoc(null);
  };

  const handleGetLink = async () => {
    setIsCopied(false); // Reset copied state when opening modal

    if (!activeEmailTemplates || activeEmailTemplates.length === 0) {
      showNotification("Please create an email template");
      return;
    }

    // If there is only one active template, pre-select it
    if (activeEmailTemplates.length === 1) {
      setSelectedGetLinkTemplate(activeEmailTemplates[0].id);
    }

    setGetLinkTemplateSelectionModalOpen(true);
  };

  const handleGetLinkTemplateSelectionContinue = async () => {
    if (!selectedGetLinkTemplate) {
      showNotification("Please select an email template.");
      return;
    }

    setGetLinkTemplateSelectionModalOpen(false);
    setLoading(true);

    try {
      // Resolve narrator info for activity logging
      let narratorNameForLog = '';
      let narratorEmailForLog = '';
      if (quicktaleDetails.narratorData) {
        narratorNameForLog = `${quicktaleDetails.narratorData.firstName || ''} ${quicktaleDetails.narratorData.lastName || ''}`.trim();
        narratorEmailForLog = quicktaleDetails.narratorData.email || '';
      } else if (quicktaleDetails.narrator) {
        // Look up custom narrator to get email
        narratorNameForLog = quicktaleDetails.narrator;
        try {
          const userIdToUse = currentUser?.userId || currentUser?.uid;
          const cn = await getCustomNarratorByName(quicktaleDetails.narrator.trim(), userIdToUse);
          if (cn) {
            narratorNameForLog = cn.name || quicktaleDetails.narrator;
            narratorEmailForLog = cn.email || '';
          }
        } catch (e) {
          console.warn('Get Link: unable to resolve custom narrator for activity logging', e);
        }
      }

      const generateExternalUpdateLink = httpsCallable(functions, "generateExternalUpdateLink");
      const result = await generateExternalUpdateLink({
        quicktaleId: quicktaleDetails.id,
        origin: window.location.origin,
        collection: "quicktales",
        emailTemplateId: selectedGetLinkTemplate,
        narratorName: narratorNameForLog,
        narratorEmail: narratorEmailForLog,
      });
      const { link } = result.data;
      if (link) {
        setGeneratedLink(link);
        setGetLinkModalOpen(true);
      } else {
        throw new Error("Could not generate link.");
      }
    } catch (error) {
      Toast("error", `Error generating link: ${error.message}`);
      console.error("Error calling generateExternalUpdateLink:", error);
    } finally {
      setLoading(false);
      setSelectedGetLinkTemplate("");
    }
  };

  const handleGetLinkTemplateSelectionClose = () => {
    setGetLinkTemplateSelectionModalOpen(false);
    setSelectedGetLinkTemplate("");
  };

  // Helper: fire-and-forget call to processAudioWithAuphonicV5 (HTTP onRequest function)
  const fireAndForgetProcessAudio = async ({ fileUrl, qtTitle, ogFileName, collectionName, documentId }) => {
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        console.error("No auth token available for background audio processing.");
        return;
      }
      const projectId = auth.app.options.projectId;
      const functionUrl = `https://us-central1-${projectId}.cloudfunctions.net/processAudioWithAuphonicV5`;
      
      fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          fileUrl,
          qtTitle: qtTitle || 'Audio Processing',
          ogFileName: ogFileName || 'audio',
          collectionName,
          documentId,
        }),
      }).catch(err => console.error('Background audio processing error:', err));
      
      console.log("Fire-and-forget audio processing initiated for:", documentId);
    } catch (err) {
      console.error("Error initiating background audio processing:", err);
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(generatedLink);
    setIsCopied(true);
    // Keep modal open, but show "Copied!" text
    setTimeout(() => {
        setGetLinkModalOpen(false);
        setIsCopied(false); // Reset for next time
    }, 1500); // Close modal after 1.5s
  };

  // Persist a "Created By" change immediately (used by the "Set to Org" button
  // in view mode, where there is no Save button). Stores just the id.
  const handleCreatedBySet = async (newCreatedById) => {
    if (!quicktaleDetails?.id || !newCreatedById) return;
    try {
      await updateQuickTale(quicktaleDetails.id, {
        createdBy: newCreatedById,
        updatedAt: new Date(),
        updatedBy: currentUser?.id || "",
      });
      showNotification("Created By updated successfully!");
    } catch (error) {
      console.error("Error updating Created By:", error);
      Toast("error", `Error updating Created By: ${error.message}`);
    }
  };

  const handleUpdateQuickTale = async () => {
    if (!quicktaleDetails.narrator) {
      setNarratorError(true);
      showNotification("Told by is a required field.");
      return;
    } else {
      setNarratorError(false);
    }
    console.log("[QuickTaleToOrganizationDetails] Save started", {
      quicktaleId: quicktaleDetails?.id,
      currentApprovalStatus: quicktaleDetails?.approvalStatus,
    });

    try {
      setSaving(true);
      
      // Check if narrator is a custom name (text) and not a user ID
      // If narratorData is null, it means they typed text or selected a custom narrator
      if (!quicktaleDetails.narratorData && quicktaleDetails.narrator) {
        const narratorName = quicktaleDetails.narrator.trim();
        
        // Get user ID to check/create custom narrators
        const userIdToUse = currentUser?.userId || currentUser?.uid;
        
        if (userIdToUse && narratorName) {
          try {
            // Fetch current custom narrators for this user
            const existingNarrators = await getCustomNarratorsByUser(userIdToUse);
            
            // Check if this narrator name already exists
            if (!narratorNameExists(narratorName, existingNarrators)) {
              // Create new custom narrator
              const newNarrator = await createCustomNarrator(narratorName, userIdToUse);
              if (newNarrator) {
                console.log("Created new custom narrator:", newNarrator);
              }
            }
          } catch (error) {
            console.error("Error checking/creating custom narrator:", error);
            // Continue with save even if custom narrator creation fails
          }
        }
      }
      
      // let tags;
      // if (quicktaleDetails.tags != location.state.quicktaleDetails.tags) {
      //   tags = quicktaleDetails.tags.split(",");
      // }
      // let dataTemp = {
      //   title: quicktaleDetails.title,
      //   description: quicktaleDetails.description,
      //   status: quicktaleDetails.status,
      //   approvalStatus: quicktaleDetails?.approvalStatus,
      //   updatedAt: new Date(),
      //   updatedBy: currentUser?.id,
      //   createdBy: quicktaleDetails.createdBy,
      //   narrator: quicktaleDetails.narrator,
      //   // location: quicktaleDetails.location,
      //   // // coordinates: quicktaleDetails.coordinates,
      //   // // g: quicktaleDetails.g,
      //   // coordinates: new GeoPoint(
      //   //   quicktaleDetails?.coordinates?._lat,
      //   //   quicktaleDetails?.coordinates?._long
      //   // ),
      //   // g: {
      //   //   geohash: quicktaleDetails?.g?.geohash,
      //   //   geopoint: new GeoPoint(
      //   //     quicktaleDetails?.coordinates?._lat,
      //   //     quicktaleDetails?.coordinates?._long
      //   //   ),
      //   // },
      //   tags:
      //     quicktaleDetails.tags === location.state.quicktaleDetails.tags
      //       ? location.state.quicktaleDetails.tags
      //       : quicktaleDetails.tags.split(","),
      // };

      // let title =
      //   quicktaleDetails.title ||
      //   `Untitled ${new Date().toLocaleDateString("en-US", {
      //     month: "2-digit",
      //     day: "2-digit",
      //     year: "numeric",
      //   })}`;
      let title =
        quicktaleDetails.title || `Untitled ${moment().format("MM-DD-YYYY")}`;
      setQuicktaleDetails({ ...quicktaleDetails, title: title });

      // Ensure approvalStatus is persisted and uses a consistent Timestamp shape.
      // (UI may display Title Case, but DB stores lowercase.)
      const approvalStatusToSave = {
        status: (quicktaleDetails?.approvalStatus?.status || "pending").toLowerCase(),
        updatedAt: Timestamp.now(),
        // Elsewhere in the codebase this is typically an id
        updatedBy: currentUser?.id || "",
      };

      let dataTemp = {
        title: title,
        // title: quicktaleDetails.title || "",
        description: quicktaleDetails.description || "",
        status: quicktaleDetails.status || "",
        approvalStatus: approvalStatusToSave,
        isDraft:
          quicktaleDetails?.isDraft !== undefined
            ? quicktaleDetails.isDraft
            : false,
        updatedAt: new Date(),
        updatedBy: currentUser?.id || "",
        createdBy: quicktaleDetails.createdBy || "",
        narrator: quicktaleDetails.narrator || "",
        location: quicktaleDetails.location || "",
        locationDisplay: quicktaleDetails.locationDisplay || "",
        locationCountry: quicktaleDetails.locationCountry || "",
        coordinates: new GeoPoint(
          quicktaleDetails?.coordinates?._lat || 0,
          quicktaleDetails?.coordinates?._long || 0
        ),
        g: {
          geohash: quicktaleDetails?.g?.geohash || "",
          geopoint: new GeoPoint(
            quicktaleDetails?.coordinates?._lat || 0,
            quicktaleDetails?.coordinates?._long || 0
          ),
        },
        // tags:
        //   quicktaleDetails.tags === location.state.quicktaleDetails.tags
        //     ? location.state.quicktaleDetails.tags
        //     : quicktaleDetails?.tags.split(","),
      };
      if (quicktaleDetails.tags !== location?.state?.quicktaleDetails?.tags) {
        if (typeof quicktaleDetails.tags === "string") {
          dataTemp.tags = quicktaleDetails.tags.split(",");
        } else if (Array.isArray(quicktaleDetails.tags)) {
          dataTemp.tags = [...quicktaleDetails.tags];
        } else {
          dataTemp.tags = [];
        }
      }
      if (uploadImage != null) {
        let photoUrl = await uploadQuickTaleImageToStorage(uploadImage?.raw);
        // dataTemp.photoUrl = photoUrl;
        dataTemp.imageUrls = [photoUrl];
        dataTemp.originalPhotoName = uploadImage?.raw?.name;
      }
      if (uploadAudioFile != null) {
        let audioUrl = await uploadQuickTaleAudioToStorage(
          uploadAudioFile?.raw
        );
        dataTemp.audioUrl = audioUrl;
        // Clear old processed audio — it will be re-generated by background processing
        dataTemp.processedAudioUrl = '';
        dataTemp.processedAudioName = '';
        dataTemp.originalAudioName = uploadAudioFile?.raw?.name;

        // Create an audio element to get the duration
        const audio = new Audio(audioUrl);
        await new Promise((resolve) => {
          audio.onloadedmetadata = () => {
            dataTemp.audioLength = Math.floor(audio.duration); // Duration in seconds
            setQuicktaleDetails({
              ...quicktaleDetails,
              audioLength: Math.floor(audio.duration),
            });
            resolve();
          };
        });
      }
      console.log("data:", dataTemp);
      let responseTemp = await updateQuickTale(quicktaleDetails.id, dataTemp);
      console.log("[QuickTaleToOrganizationDetails] updateQuickTale done", responseTemp);

      // Persist area membership changes (add/remove this QT on the area docs).
      // Mirrors the behavior of the regular QuickTale Detail page.
      try {
        const orgId = quicktaleDetails?.organization;
        const qtId = quicktaleDetails?.id;
        if (orgId && qtId) {
          const currentAreaIds = Array.isArray(quicktaleDetails?.uiAreaIds)
            ? quicktaleDetails.uiAreaIds
            : [];
          const { added, removed } = getAreaMembershipDiff({
            initialAreaIds: initialAreaIdsRef.current || [],
            currentAreaIds,
          });

          if (added.length || removed.length) {
            const snapshot = await getOrgAreas(orgId);
            const allAreas =
              snapshot?.docs?.map((d) => ({ id: d.id, ...d.data() })) || [];

            for (const areaId of removed) {
              const area = allAreas.find((a) => a.id === areaId);
              if (!area) continue;
              const updated = removeQuickTaleFromAlbumsAndQTs({
                albumsAndQTs: area?.albumsAndQTs || [],
                qtId,
              });
              await updateArea(areaId, { albumsAndQTs: updated, updatedAt: new Date() });
            }

            for (const areaId of added) {
              const area = allAreas.find((a) => a.id === areaId);
              if (!area) continue;
              const updated = upsertQuickTaleInAlbumsAndQTs({
                albumsAndQTs: area?.albumsAndQTs || [],
                qtId,
              });
              await updateArea(areaId, { albumsAndQTs: updated, updatedAt: new Date() });
            }

            // Reset baseline so subsequent saves diff correctly.
            initialAreaIdsRef.current = currentAreaIds;
          }
        }
      } catch (areaError) {
        console.error("Error persisting area membership:", areaError);
        // Continue — QT itself already saved successfully.
      }

      // Essential work (QT update + area membership) is complete — stop the
      // Save spinner and exit edit mode now so the UI doesn't appear stuck
      // while the non-critical AddSearch / list-refresh calls run below.
      setSaving(false);
      setEditEnabled(false);

      // let addSearchTemp = await insertQTAddSearchDocumentData({

      //   docId: quicktaleDetails.id,
      //   data: {
      //     ...dataTemp,
      //     date_updated: new Date(),
      //     // createdAt: quicktaleDetails.createdAt,
      //     environment: process.env.REACT_APP_PROJECT_ENVIROMENT,
      //   },
      //   docType: "org quicktale",
      // });
      await handleGetAllQuickTales();
      console.log("[QuickTaleToOrganizationDetails] handleGetAllQuickTales done");
      showNotification("QuickTale updated successfully.");
      // console.log("update quicktale response:", responseTemp);
      // Toast("success", "QuickTale updated successfully.");
      try {
        console.log({
          docId: quicktaleDetails.id,
          docData: {
            ...dataTemp,
            date_updated: new Date(),
            creator: quicktaleDetails.createdBy,
            environment: process.env.REACT_APP_PROJECT_ENVIROMENT,
          },
          docType: "quicktale",
        });
        let addSearchTemp = await insertQTAddSearchDocumentData({
          docId: quicktaleDetails.id,
          docData: {
            ...dataTemp,
            date_updated: new Date(),
            // date_updated: quicktaleDetails.createdAt,
            environment: process.env.REACT_APP_PROJECT_ENVIROMENT,
          },
          docType: "org quicktale",
        });
        console.log("response add search:", addSearchTemp);
        console.log("update quicktale response:", responseTemp);
      } catch (error) {
        console.error("Error inserting QT add search document data:", error);
        // throw error;
      }

      // Fire-and-forget: process audio in the background via V5
      // Triggers when: (a) there's an audioUrl but no processedAudioUrl, OR
      //                (b) new audio was uploaded in this edit session
      const audioUrlToProcess = dataTemp.audioUrl || quicktaleDetails.audioUrl;
      if (audioUrlToProcess && (!dataTemp.processedAudioUrl || uploadAudioFile != null)) {
        console.log("Initiating background audio processing via processAudioWithAuphonicV5");
        fireAndForgetProcessAudio({
          fileUrl: audioUrlToProcess,
          qtTitle: title,
          ogFileName: dataTemp.originalAudioName || quicktaleDetails.originalAudioName,
          collectionName: 'quicktales',
          documentId: quicktaleDetails.id,
        });
      }
    } catch (error) {
      Toast("error", error.message);
      console.log("error updating quicktale", error.message);
    } finally {
      console.log("[QuickTaleToOrganizationDetails] Save finished (finally)");
      setSaving(false);
      setEditEnabled(false);
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={
          quicktaleDetails && quicktaleDetails.title && quicktaleDetails.title
        }
        bgColor="#fff"
        buttonText={"Edit"}
        editEnabled={editEnabled}
        setEditEnabled={setEditEnabled}
        cancelButton={editEnabled ? true : false}
        onCancelClick={handleCancelClick}
        handleSave={() => handleUpdateQuickTale()}
        loading={saving}
        showSendLink={!editEnabled}
        onSendLink={handleSendLink}
        showGetLink={!editEnabled}
        onGetLink={handleGetLink}
        hideBackArrow={editEnabled}
      />
      <Sidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"} style={subContainerStyles}>
          {/*Top card/container section*/}
          <DetailsPageTopCardSection
            quicktaleDetails={quicktaleDetails}
            setQuicktaleDetails={setQuicktaleDetails}
            editEnabled={editEnabled}
          />
          {/*Bottom card/container section*/}
          <DetailsPageBottomCardSection
            quicktaleDetails={quicktaleDetails}
            setQuicktaleDetails={setQuicktaleDetails}
            handleImagePreviewModalOpen={handleImagePreviewModalOpen}
            handleAudioModalOpen={handleAudioModalOpen}
            editEnabled={editEnabled}
            uploadImage={uploadImage}
            setUploadImage={setUploadImage}
            uploadAudioFile={uploadAudioFile}
            setUploadAudioFile={setUploadAudioFile}
            narratorError={narratorError}
            currentOrganization={currentOrganization}
            onCreatedBySet={handleCreatedBySet}
          />

          {/* Narrator Activity section */}
          <Grid
            container
            direction={"column"}
            sx={{
              mt: 4,
              pt: 3,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 12, fontFamily: "Avenir Book, sans-serif" }}>
              Contributor Request Activity
            </h3>
            <ShareActivityTable
              events={shareActivityEvents}
              loading={shareActivityLoading}
              templates={allEmailTemplates}
            />
          </Grid>
          <div style={{marginBottom: 100}}></div>
        </Grid>
      </Grid>
      <ImagePreviewModal
        modalOpen={imagePreviewModal}
        handleClose={handleImagePreviewModalClose}
        image={
          uploadImage != null && uploadImage.preview
            ? uploadImage.preview
            : quicktaleDetails.photoUrl || (quicktaleDetails.imageUrls && quicktaleDetails.imageUrls[0])
        }
      />
      <AudioModal
        modalOpen={audioModal}
        handleClose={handleAudioModalClose}
        audioFile={
          uploadAudioFile != null && uploadAudioFile.preview
            ? uploadAudioFile.preview
            : quicktaleDetails.audioUrl
        }
      />
      <ConfirmationModal
        open={cancelConfirmationModalOpen}
        onClose={() => setCancelConfirmationModalOpen(false)}
        onConfirm={handleConfirmCancel}
        title="Discard Changes"
        message="Are you sure you want to cancel? All unsaved changes will be lost."
      />
      {/* Template Selection Modal - for when narrator already has email */}
      <ConfirmationModal
        open={templateSelectionModalOpen}
        onClose={handleTemplateSelectionClose}
        onConfirm={handleTemplateSelectionContinue}
        title="Select Email Template"
        message="Please select a template for your email."
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
        showTemplateDropdown={true}
        templates={activeEmailTemplates}
        selectedTemplate={selectedTemplate}
        onTemplateChange={(e) => setSelectedTemplate(e.target.value)}
        noTemplatesMessage="Please create an email template"
        // When narrator is a registered user, show their details as read-only
        narratorName={
          quicktaleDetails.narratorData
            ? quicktaleDetails.narratorData.userName
              ? `@${quicktaleDetails.narratorData.userName}`
              : `${quicktaleDetails.narratorData.firstName || ""} ${
                  quicktaleDetails.narratorData.lastName || ""
                }`.trim()
            : undefined
        }
        firstName={quicktaleDetails.narratorData?.firstName}
        lastName={quicktaleDetails.narratorData?.lastName}
        textFieldValue={quicktaleDetails.narratorData?.email}
        narratorReadOnly={!!quicktaleDetails.narratorData}
      />
      {/* Email Prompt Modal - for when narrator is missing details (with template dropdown) */}
      <ConfirmationModal
        open={emailPromptModalOpen}
        onClose={handleEmailPromptClose}
        onConfirm={handleEmailPromptContinue}
        title="Narrator Details Required"
        message="Please enter the missing details needed to send the update link."
        textFieldValue={narratorEmail}
        onTextFieldChange={(e) => setNarratorEmail(e.target.value)}
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
        narratorName={customNarratorDoc?.name || quicktaleDetails.narrator}
        firstName={narratorFirstName}
        onFirstNameChange={(e) => setNarratorFirstName(e.target.value)}
        lastName={narratorLastName}
        onLastNameChange={(e) => setNarratorLastName(e.target.value)}
        showTemplateDropdown={true}
        templates={activeEmailTemplates}
        selectedTemplate={selectedTemplate}
        onTemplateChange={(e) => setSelectedTemplate(e.target.value)}
        noTemplatesMessage="Please create an email template"
      />

      {/* Template Selection Modal - for Get Link (link-only, no send) */}
      <ConfirmationModal
        open={getLinkTemplateSelectionModalOpen}
        onClose={handleGetLinkTemplateSelectionClose}
        onConfirm={handleGetLinkTemplateSelectionContinue}
        title="Select Email Template"
        message="Please select a template for the narrator page settings."
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
        showTemplateDropdown={true}
        templates={activeEmailTemplates}
        selectedTemplate={selectedGetLinkTemplate}
        onTemplateChange={(e) => setSelectedGetLinkTemplate(e.target.value)}
        noTemplatesMessage="Please create an email template"
      />

      {/* Get Link Modal */}
      <ConfirmationModal
        open={getLinkModalOpen}
        onClose={() => setGetLinkModalOpen(false)}
        onConfirm={handleCopyToClipboard}
        title="Link to update QuickTale photo, audio, title, and/or description"
        message=""
        confirmButtonText={isCopied ? "Copied!" : "Copy"}
        cancelButtonText="Close"
        showTextField={true}
        textFieldValue={generatedLink}
      />
    </Box>
  );
};

export default QuickTaleToOrganizationDetailsContainer;

const subContainerStyles = {
  paddingLeft: 30,
  paddingRight: 30,
  marginTop: 50,
};
